import * as THREE from 'three';
import { hash01, smoothstep, valueNoise } from '@/core/rng';
import { idSeed, type StructureView } from './structures';

/**
 * Habitation seen from far enough away to look for it (spec §3, §13).
 *
 * At a hundred and fifty metres a camp reads perfectly: `world/wear.ts` packs
 * the ground bare under it, fells the mid-storey, and leaves a clearing with
 * shelters in it. But wear is a *chunk* transform, and chunks stop at the
 * streamed ring — two and a half to four kilometres out. Past that the ground
 * is `horizon.ts`, one coarse mesh that has never heard of the mortal-pressure
 * ledger. So the exact thing a god flies up to look for — where are the
 * people? — stops existing at precisely the altitude they fly up to.
 *
 * This carries the clearing the rest of the way: one soft patch per camp, over
 * both the streamed ground and the coarse mesh, fading in as the eye pulls
 * back and gone by the time the real wear can speak for itself. It invents no
 * history — the patch is drawn where the structures actually stand, sized to
 * how far they actually spread, and told apart by whether a hearth there is
 * actually lit.
 *
 * **An abandoned camp is drawn differently, not the same and fainter.** A
 * clearing is what living use makes: the pressure ledger forgets a place ten
 * days after the last foot, and fifty years on there is no bare ground left
 * to draw. What an old site keeps is the *shape* of having been lived on —
 * rough ground where the packed earth was, a different green, the ruins still
 * standing in it. So a living camp gets bare earth and a dead one gets that
 * rough greener ground: from the air the two never read alike, and a god
 * crossing the island can tell where the people are from where they *were*.
 * Which is the whole of §3 in one glance.
 *
 * Render-only. Reads views; owns nothing; writes nothing back.
 */

export interface CampView {
  id: string;
  x: number; y: number; z: number;
  /** Metres of cleared ground — the camp's own spread, plus its margin. */
  radius: number;
  /** Downhill gradient at the camp, so the patch lies on the slope it is on. */
  gx: number; gz: number;
  /** Is a hearth here still someone's? A dead camp is drawn as old ground. */
  lit: boolean;
}

export interface Habitation {
  group: THREE.Group;
  update(camps: CampView[]): void;
}

/** Camps in view at once. Twenty-eight on a century-old island; this is slack. */
const CAP = 96;
/**
 * Trodden earth — the same colour `world/wear.ts` packs the ground toward, so
 * the patch and the wear underneath it are the same brown, and crossing out of
 * the streamed ring changes nothing about what the camp looks like.
 */
const PACKED = [0.30, 0.25, 0.18] as const;
/**
 * And old ground: the dry, rough, faintly yellow green that comes back over a
 * site nobody has walked on for a lifetime. Deliberately close enough to the
 * living grass to be *almost* nothing — an old camp should be a thing you
 * notice, not a thing that announces itself.
 */
const OLD_GROUND = [0.32, 0.33, 0.19] as const;
/**
 * Where the patch takes over. Inside FADE_NEAR the real wear draws the
 * clearing and this would only paint over it; by FADE_FAR the chunks
 * underfoot are coarse or gone and this is all there is.
 */
const FADE_NEAR = 600, FADE_FAR = 1500;
/** How much of the ground it covers: a clearing is bare earth, not a stain. */
const STRENGTH = 0.82;
/** …and how much of it a lifetime of regrowth has taken back. */
const RUIN_STRENGTH = 0.42;
/** A ruin's ground reads a little wider than the camp did: the edges blur. */
const RUIN_SPREAD = 1.15;
/**
 * Metres of clearance over the ground. The tilt takes out the slope itself, so
 * what is left to clear is the ground's curvature across the patch — which
 * scales with how wide the patch is, hence the second term.
 */
const LIFT = 1.0, LIFT_PER_RADIUS = 0.035;

/**
 * The clearing, as an alpha texture: a soft disc with a ragged margin and a
 * slightly darker middle where the fire and the feet are.
 *
 * The raggedness is the point. `wear.ts` breaks the edge of its own clearing
 * with value noise finer than the lattice, for the reason it gives there — a
 * hard threshold on a smooth field draws a disc, and nothing in a landscape
 * is a disc. This is the same trick at the same scale, so the far patch and
 * the near wear have the same kind of edge.
 */
function clearingTexture(): THREE.DataTexture {
  const s = 128;
  const data = new Uint8Array(s * s * 4);
  for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) {
    const u = (x + 0.5) / s * 2 - 1, v = (y + 0.5) / s * 2 - 1;
    const r = Math.hypot(u, v);
    // two scales of noise on the radius: broad lobes, then a frayed rim
    const wobble = (valueNoise(u * 3.1, v * 3.1, 0x51a7) - 0.5) * 0.30
                 + (valueNoise(u * 11.0, v * 11.0, 0x93c2) - 0.5) * 0.16;
    const a = smoothstep(1, 0.52, r + wobble);
    const k = (y * s + x) * 4;
    // the middle is packed harder than the margin — a camp is worn from
    // the fire outward, and that gradient is what makes the patch read as
    // a place rather than as a stain
    const core = 1 - 0.28 * smoothstep(0.55, 0.05, r);
    data[k] = data[k + 1] = data[k + 2] = Math.round(255 * core);
    data[k + 3] = Math.round(255 * a);
  }
  const tex = new THREE.DataTexture(data, s, s);
  tex.magFilter = tex.minFilter = THREE.LinearFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Which structures stand together as one camp, and where its middle is.
 *
 * Single-link at CLUSTER metres: a shelter sixty metres from the fire belongs
 * to the fire. This is a *renderer's* reading of the ground — the simulation
 * has no settlement container yet (Expansion II §49), and when it grows one
 * this function is what it replaces. Deliberately pure and order-independent
 * so it can be tested against exactly that.
 */
const CLUSTER = 120;

export interface Camp {
  id: string;
  x: number; z: number;
  /** Metres from the middle to the outermost structure. */
  spread: number;
  /** Is a hearth here still someone's? */
  lit: boolean;
  members: number;
}

export function campsFrom(
  structures: readonly StructureView[],
  isLit: (s: StructureView) => boolean = (s) => s.type === 'hearth' && s.active,
): Camp[] {
  const n = structures.length;
  const parent = new Int32Array(n).map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; }
    return i;
  };
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
    const a = structures[i], b = structures[j];
    if (Math.hypot(a.x - b.x, a.z - b.z) > CLUSTER) continue;
    const ra = find(i), rb = find(j);
    if (ra !== rb) parent[ra < rb ? rb : ra] = ra < rb ? ra : rb;
  }
  const groups = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    const g = groups.get(r);
    if (g) g.push(i); else groups.set(r, [i]);
  }
  const camps: Camp[] = [];
  for (const idx of groups.values()) {
    let x = 0, z = 0, lit = false;
    let id = structures[idx[0]].id;
    for (const i of idx) {
      const s = structures[i];
      x += s.x; z += s.z;
      if (isLit(s)) lit = true;
      if (s.id < id) id = s.id;             // stable whatever order they arrive in
    }
    x /= idx.length; z /= idx.length;
    let spread = 0;
    for (const i of idx) {
      spread = Math.max(spread, Math.hypot(structures[i].x - x, structures[i].z - z));
    }
    camps.push({ id, x, z, spread, lit, members: idx.length });
  }
  camps.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return camps;
}

/**
 * Metres of cleared ground a camp of this spread makes.
 *
 * Measured on the century-old island (`tools/settlement.ts`): structures
 * spread a median 23 m from the middle, and the mortal-pressure ledger still
 * reads above wear's TRACE threshold out to 64 m — so the clearing runs about
 * forty metres wider than the buildings do, which is what this says. Small
 * camps get the floor, because three shelters in a row still clear the ground
 * they sit on.
 */
export const clearingRadius = (spread: number) => Math.max(52, spread + 42);

export function createHabitation(): Habitation {
  const group = new THREE.Group();
  const geo = new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2);
  const mat = new THREE.MeshStandardMaterial({
    map: clearingTexture(),
    transparent: true, depthWrite: false, roughness: 1, metalness: 0,
    side: THREE.DoubleSide,
    polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3,
  });
  // how much ground each patch actually covers — bare earth where people
  // live, a whisper of rough green where they used to
  const strength = new Float32Array(CAP);
  geo.setAttribute('aStrength', new THREE.InstancedBufferAttribute(strength, 1));
  /**
   * The patch belongs to the distance, so the distance switches it on. Done
   * per fragment against the camera, which means a patch straddling the band
   * fades across itself rather than popping as a whole — and it needs no
   * knowledge, on this side of the wire, of where the camera is.
   */
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uFade = { value: new THREE.Vector2(FADE_NEAR, FADE_FAR) };
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>',
        '#include <common>\nattribute float aStrength;\nvarying vec3 vCampW;\nvarying float vStrength;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vStrength = aStrength;
        vCampW = (modelMatrix * instanceMatrix * vec4(position, 1.0)).xyz;`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>',
        '#include <common>\nvarying vec3 vCampW;\nvarying float vStrength;\nuniform vec2 uFade;')
      .replace('#include <alphatest_fragment>', `
        diffuseColor.a *= vStrength * smoothstep(uFade.x, uFade.y,
          distance(cameraPosition, vCampW));
        #include <alphatest_fragment>`);
  };

  const mesh = new THREE.InstancedMesh(geo, mat, CAP);
  mesh.name = 'clearings';
  mesh.frustumCulled = false;
  mesh.receiveShadow = false;
  mesh.count = 0;
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  const white = new THREE.Color(1, 1, 1);
  for (let i = 0; i < CAP; i++) mesh.setColorAt(i, white);
  mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
  group.add(mesh);

  const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler();
  const _tilt = new THREE.Quaternion(), _n = new THREE.Vector3();
  const _p = new THREE.Vector3(), _s = new THREE.Vector3(), _c = new THREE.Color();
  const UP = new THREE.Vector3(0, 1, 0);

  return {
    group,
    update(camps) {
      let n = 0;
      for (const c of camps) {
        if (n >= CAP) break;
        // lie on the slope rather than through it: the ground's own normal
        // tips the patch, so a camp on a hillside keeps its whole outline
        // instead of half of it disappearing into the hill
        _n.set(-c.gx, 1, -c.gz).normalize();
        _tilt.setFromUnitVectors(UP, _n);
        // one hashed turn per camp, so no two clearings wear the same noise
        _e.set(0, hash01(idSeed(c.id), 3, 0x2f13) * Math.PI * 2, 0);
        _q.setFromEuler(_e).premultiply(_tilt);
        const r = c.lit ? c.radius : c.radius * RUIN_SPREAD;
        _p.set(c.x, c.y + LIFT + r * LIFT_PER_RADIUS, c.z);
        _s.set(r, 1, r);
        _m.compose(_p, _q, _s);
        mesh.setMatrixAt(n, _m);
        const tone = c.lit ? PACKED : OLD_GROUND;
        mesh.setColorAt(n, _c.setRGB(tone[0], tone[1], tone[2]));
        strength[n] = c.lit ? STRENGTH : RUIN_STRENGTH;
        n++;
      }
      mesh.count = n;
      mesh.visible = n > 0;
      mesh.instanceMatrix.needsUpdate = true;
      mesh.instanceColor!.needsUpdate = true;
      (geo.getAttribute('aStrength') as THREE.BufferAttribute).needsUpdate = true;
    },
  };
}
